/**
 * Product card behaviour (v2) — shop cards (.pcs) get variant-chip switching
 * + add-to-cart via /cart/add.js; catalog cards (.pcc) get a localStorage-backed
 * sample-box toggle. Deliberately does not touch Horizon's internal cart-event
 * system — plain fetch + a local success state instead.
 */
(function () {
  'use strict';

  var SAMPLES_KEY = 'fixplus_samples';

  function readSamples() {
    try {
      var raw = window.localStorage.getItem(SAMPLES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function writeSamples(ids) {
    try {
      window.localStorage.setItem(SAMPLES_KEY, JSON.stringify(ids));
    } catch (e) {
      /* storage unavailable — sample state just won't persist */
    }
  }

  function initSampleToggle(button) {
    var productId = button.getAttribute('data-product-id');
    var samples = readSamples();
    if (samples.indexOf(productId) > -1) {
      button.setAttribute('data-added', 'true');
    }

    button.addEventListener('click', function () {
      var ids = readSamples();
      var index = ids.indexOf(productId);
      var added;

      if (index > -1) {
        ids.splice(index, 1);
        added = false;
      } else {
        ids.push(productId);
        added = true;
      }

      writeSamples(ids);
      button.setAttribute('data-added', added ? 'true' : 'false');

      document.dispatchEvent(
        new CustomEvent('fixplus:sample-toggle', {
          bubbles: true,
          detail: {
            productId: productId,
            productTitle: button.getAttribute('data-product-title'),
            added: added,
            count: ids.length,
          },
        })
      );
    });
  }

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withSeparators = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formatted = withSeparators + '.' + parts[1];
    if (format && format.indexOf('{{amount}}') > -1) {
      return format.replace('{{amount}}', formatted);
    }
    return '€' + formatted;
  }

  function initShopCard(card) {
    var dataScript = card.querySelector('[data-role="variant-data"]');
    if (!dataScript) return;

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    var variants = data.variants || [];
    var groups = card.querySelectorAll('[data-role="variant-group"]');
    var selected = {};

    groups.forEach(function (group) {
      var activeChip = group.querySelector('.pcs__variant-chip.is-active');
      var optionIndex = group.getAttribute('data-option-index');
      selected[optionIndex] = activeChip
        ? activeChip.getAttribute('data-value')
        : group.querySelector('.pcs__variant-chip').getAttribute('data-value');
    });

    function findMatchingVariant() {
      var optionIndexes = Object.keys(selected).sort();
      return variants.filter(function (variant) {
        return optionIndexes.every(function (idx) {
          return variant.options[Number(idx)] === selected[idx];
        });
      })[0];
    }

    function applyVariant(variant) {
      if (!variant) return;

      var priceEl = card.querySelector('[data-role="price"]');
      var comparePriceEl = card.querySelector('[data-role="compare-price"]');
      var saveBadgeEl = card.querySelector('[data-role="save-badge"]');
      var skuEl = card.querySelector('[data-role="sku"]');
      var stockBadgeEl = card.querySelector('[data-role="stock-badge"]');
      var variantIdInput = card.querySelector('[data-role="variant-id-input"]');
      var addToCartBtn = card.querySelector('[data-role="add-to-cart"]');
      var cardImage = card.querySelector('[data-role="card-image"]');

      if (priceEl) priceEl.textContent = formatMoney(variant.price, data.moneyFormat);

      if (variant.compare_at_price > variant.price) {
        if (comparePriceEl) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price, data.moneyFormat);
          comparePriceEl.hidden = false;
        }
        if (saveBadgeEl) {
          saveBadgeEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price, data.moneyFormat);
          saveBadgeEl.hidden = false;
        }
      } else {
        if (comparePriceEl) comparePriceEl.hidden = true;
        if (saveBadgeEl) saveBadgeEl.hidden = true;
      }

      if (skuEl) skuEl.textContent = variant.sku || '';
      if (variantIdInput) variantIdInput.value = variant.id;

      if (cardImage && variant.featured_image) {
        cardImage.src = variant.featured_image;
      }

      if (stockBadgeEl) {
        stockBadgeEl.classList.remove('pcs__stock--low');
        var dot = stockBadgeEl.querySelector('.pcs__stock-dot');
        if (!variant.available) {
          stockBadgeEl.lastChild.textContent = ' Sold out';
          stockBadgeEl.style.color = '#8B8B8B';
          if (dot) dot.style.background = '#8B8B8B';
        } else if (
          variant.inventory_management &&
          variant.inventory_quantity <= 10 &&
          variant.inventory_quantity > 0
        ) {
          stockBadgeEl.classList.add('pcs__stock--low');
          stockBadgeEl.style.color = '';
          if (dot) dot.style.background = '';
          stockBadgeEl.lastChild.textContent = ' Only ' + variant.inventory_quantity + ' left';
        } else {
          stockBadgeEl.style.color = '';
          if (dot) dot.style.background = '';
          stockBadgeEl.lastChild.textContent = ' In stock';
        }
      }

      if (addToCartBtn) {
        addToCartBtn.disabled = !variant.available;
      }

      card.classList.toggle('pcs--sold-out', !variant.available);
    }

    groups.forEach(function (group) {
      var optionIndex = group.getAttribute('data-option-index');
      group.querySelectorAll('.pcs__variant-chip').forEach(function (chip) {
        chip.addEventListener('click', function () {
          selected[optionIndex] = chip.getAttribute('data-value');

          group.querySelectorAll('.pcs__variant-chip').forEach(function (c) {
            c.classList.toggle('is-active', c === chip);
          });

          applyVariant(findMatchingVariant());
        });
      });
    });

    var qtyBtn = card.querySelector('[data-role="qty-display"]');
    var qty = 1;
    if (qtyBtn) {
      qtyBtn.addEventListener('click', function () {
        qty = qty >= 9 ? 1 : qty + 1;
        qtyBtn.textContent = qty;
      });
    }

    var form = card.querySelector('[data-role="add-form"]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();

        var addToCartBtn = form.querySelector('[data-role="add-to-cart"]');
        var variantIdInput = form.querySelector('[data-role="variant-id-input"]');
        if (!variantIdInput || !variantIdInput.value) return;

        var originalHTML = addToCartBtn ? addToCartBtn.innerHTML : '';
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Adding…';
        }

        var cartComponent = document.querySelector('cart-items-component[data-section-id]');
        var cartSectionId = cartComponent && cartComponent.getAttribute('data-section-id');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: variantIdInput.value,
            quantity: qty,
            sections: cartSectionId || undefined,
          }),
        })
          .then(function (response) {
            return response.json().then(function (body) {
              if (!response.ok) throw new Error(body.description || 'Could not add to cart');
              return body;
            });
          })
          .then(function (body) {
            if (addToCartBtn) {
              addToCartBtn.textContent = 'Added ✓';
            }
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { sections: body.sections } }));
            window.setTimeout(function () {
              if (addToCartBtn) {
                addToCartBtn.disabled = false;
                addToCartBtn.innerHTML = originalHTML;
              }
            }, 1800);
          })
          .catch(function (error) {
            if (addToCartBtn) {
              addToCartBtn.disabled = false;
              addToCartBtn.textContent = 'Try again';
              window.setTimeout(function () {
                addToCartBtn.innerHTML = originalHTML;
              }, 1800);
            }
            console.error('[fixplus] add to cart failed:', error.message);
          });
      });
    }
  }

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('.pcc [data-sample-toggle]').forEach(initSampleToggle);
    root.querySelectorAll('.pcs').forEach(initShopCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () { init(); });

  // Exposed so code that injects product cards dynamically (e.g. the
  // recommendation-fragment fetch in product-crosssell.js) can wire up the
  // new cards' sample toggles / variant chips without duplicating this logic.
  window.fixplusInitProductCards = init;
})();
